import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { adminBibliotecaPlantilla, adminPlantilla, getBibliotecaPlantillas, getPlantilla, metaMediaUpsert, uploadMedia } from "Services/meta_service";
import { adminContenidoMicroaprendizajes } from "Services/microaprendizaje_service";
import { fetchContenidoMicroaprendizaje } from "Slices/Microaprendizaje/microaprendizajeSlice";
import { setResponse } from "Slices/Response/responseSlice";
import { setToast, setLoading, setModal } from "Slices/uiSlice";


const initialState = {
  plantillas: [],
  plantilla: {
    idPlantilla: null,
    titulo: '',
    estado: 1,
    header_tipo: null,
    idMedia: null,
    media_caption: '',
    body_text: '',
    s3_filename: '',
    mime: '',
    botones_json: [],
    botones: [],
  },
  biblioteca: [],
  itemBiblioteca: {}
}

export const metaSlice = createSlice({
  name: 'meta',
  initialState,
  reducers: {
    setPlantillas: (state, action) => {
      state.plantillas = action.payload;
    },
    setPlantilla: (state, action) => {
      state.plantilla = action.payload ?? initialState.plantilla;
    },
    updatePlantillaField: (state, action) => {
      const { name, value } = action.payload;
      state.plantilla[name] = value;
    },
    setBiblioteca: (state, action) => {
      state.biblioteca = action.payload;
    },
    setItemBiblioteca: (state, action) => {
      state.itemBiblioteca = action.payload;
    },
    updateBibliotecaField: (state, action) => {
      const { name, value } = action.payload;
      state.biblioteca[name] = value;
    },
  }
});

export const fetchPlantilla = createAsyncThunk(
  'meta/fetchPlantilla',
  async (data, { dispatch }) => {
    const metaRes = await getPlantilla({ idPlantilla: data.idPlantilla });
    dispatch(setPlantilla(metaRes));
  }
);

export const fetchBibliotecaPlantilla = createAsyncThunk(
  'meta/fetchBibliotecaPlantilla',
  async (data, { dispatch }) => {
    const bibliotecaRes = await getBibliotecaPlantillas({ codPlantilla: data.idPlantilla });
    dispatch(setBiblioteca(bibliotecaRes));
  }
);

export const mergePlantilla = createAsyncThunk(
  'meta/plantilla/merge',
  async (data, { dispatch }) => {
    dispatch(setLoading(true));

    const _uploadMedia = await uploadMedia(data);

    if (_uploadMedia?.result) {
      const bibliotecaRes = await adminBibliotecaPlantilla(data);
      dispatch(setResponse(bibliotecaRes));
    }

    dispatch(setLoading(false));
    dispatch(setModal(false));
    dispatch(setToast(true));
    // dispatch(fetchPlantilla({idPlantilla: data.idPlantilla ?? metaRes.data}));
    setTimeout(() => {
      dispatch(setToast(false));
    }, 3000);
  }
);

export const mergeItemBiblioteca = createAsyncThunk(
  'meta/biblioteca/merge',
  async (data, { dispatch }) => {
    dispatch(setLoading(true));

    const _uploadMedia = await uploadMedia(data);

    if (_uploadMedia?.result) {
      const metaRes = await adminPlantilla(data);
      dispatch(setResponse(metaRes));
    }

    dispatch(setLoading(false));
    dispatch(setModal(false));
    dispatch(setToast(true));
    // dispatch(fetchPlantilla({idPlantilla: data.idPlantilla ?? metaRes.data}));
    setTimeout(() => {
      dispatch(setToast(false));
    }, 3000);
  }
);

/**
 * Util: construye payload de plantilla desde el item del form + respuesta de upload
 * uploadRes: el objeto .data que retorna el endpoint de upload (metaId, name, mime, tipo, s3)
 */
const buildPlantillaPayloadFromItem = (item, uploadResData) => {
  console.log("Building plantilla payload from item:", item, "and upload data:", uploadResData);
  const draft = item.plantillaDraft || {};
  const header_tipo = draft.header_tipo || item.header_tipo || "N";
  const botones = Array.isArray(draft.botones) ? draft.botones : (item.botones || []);
  const botones_json = JSON.stringify(botones);

  return {
    idPlantilla: item.idPlantilla ?? null,
    titulo: item.titulo || "Plantilla",
    estado: typeof item.estado === "number" ? item.estado : 1,
    header_tipo,
    header_text: header_tipo === "T" ? (item.header_text ?? "") : null,
    idMedia: (header_tipo === "I" || header_tipo === "V") ? uploadResData?.metaId : null,
    s3_filename: uploadResData?.name || null,
    mime: uploadResData?.mime || null,
    media_caption: item.media_caption ?? "",
    body_text: item.body_text ?? "",
    wa_media_id: item.wa_media_id || (uploadResData?.metaId || null),
    botones_json,
    botones,
  };
};

// -------- Helpers --------
const isInt = (v) => Number.isInteger(typeof v === "string" ? Number(v) : v);
const toUpper = (v) => (v ? String(v).trim().toUpperCase() : v);

// Limpia y normaliza campos de media en el payload según reglas del SP
function normalizePlantillaPayload(payload, uploadData, prevHeaderTipo) {
  const tipo = toUpper(payload.header_tipo) || "N";
  const prevTipo = toUpper(prevHeaderTipo) || tipo;

  // 1) Si subiste archivo nuevo -> siempre usar su WA ID (uploadData.metaId)
  if (uploadData?.metaId) {
    payload.wa_media_id = String(uploadData.metaId);
    delete payload.idMedia; // Evita mandar un WA ID en idMedia (INT)
  } else {
    // Si no hubo upload nuevo:
    // - Si viene idMedia pero no es INT, lo borramos (para que el SP no trate de castear basura).
    if (payload.idMedia && !isInt(payload.idMedia)) {
      delete payload.idMedia;
    }
    // - Si por error vienen ambos, priorizamos wa_media_id y quitamos idMedia.
    if (payload.wa_media_id) {
      delete payload.idMedia;
    }
  }

  // 2) Para I/V, header_text debe ir null
  if (tipo === "I" || tipo === "V") {
    payload.header_text = null;
  }

  // 3) Si el usuario cambió el tipo (p.ej., de V -> I), purgamos restos viejos si no hubo upload
  if (prevTipo && tipo !== prevTipo && !uploadData?.metaId) {
    // Limpiamos media para forzar coherencia (evita arrastrar el WA ID del asset anterior)
    delete payload.wa_media_id;
    delete payload.idMedia;
  }

  // 4) Quitar decorativos que el SP no usa (evita confusiones en logs)
  delete payload.mime;
  delete payload.s3_filename;
  delete payload.botones_json; // el SP usa $.botones, no botones_json

  // 5) Coerciones seguras
  if (typeof payload.estado !== "number") {
    payload.estado = Number(payload.estado ?? 1) === 1 ? 1 : 0;
  }
  payload.titulo = payload.titulo?.toString?.().trim() || "Plantilla";

  return payload;
}

/**
 * Thunk: sube media (Meta+S3) y crea/actualiza la plantilla; luego adjunta la plantilla al contenido del micro.
 * Espera: { item } donde item es la fila activa del form (con file, plantillaDraft, etc.)
 */
export const createContenidoFromPlantilla = createAsyncThunk(
  "meta/plantilla/createContenidoFromPlantilla",
  async ({ item }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      if (!item) throw new Error("No hay item seleccionado.");
      if (item?.formato !== ".plantilla") throw new Error("El contenido no es de tipo plantilla.");

      const isEdit = !!(item?.plantilla?.idPlantilla || item?.idPlantilla);

      // helper local igual que en el módulo viejo
      const isRealFile = (f) =>
        (typeof File !== "undefined" && f instanceof File) ||
        (typeof Blob !== "undefined" && f instanceof Blob);

      // Header tipo actual vs anterior (si venís de edición)
      const currentHeaderTipo =
        toUpper(item?.header_tipo) ||
        toUpper(item?.plantilla?.header_tipo) ||
        toUpper(item?.plantillaDraft?.header_tipo) ||
        "N";

      const prevHeaderTipo = toUpper(item?.plantilla?.header_tipo) || currentHeaderTipo;

      // -------- 1) Upload de media si hay archivo nuevo --------
      let uploadData = null;

      if (item?.file && isRealFile(item.file)) {
        // armamos multipart/form-data CORRECTO
        const fd = new FormData();

        const filename = item.file.name || "upload.bin";

        // clave exacta "file" y filename explícito
        fd.append("file", item.file, filename);

        // tu API espera este campo extra
        fd.append("tipo", "contenido");

        // (debug opcional)
        // for (const [k,v] of fd.entries()) { console.log('FD', k, v); }

        const uploadResp = await uploadMedia(fd);

        const ok = !!(uploadResp?.data?.result ?? uploadResp?.result);
        if (!ok) {
          const msg =
            uploadResp?.data?.message ||
            uploadResp?.message ||
            "Falló la carga del media.";
          throw new Error(msg);
        }

        // respuesta esperada: { metaId, name, mime, ... }
        uploadData = uploadResp?.data?.data ?? uploadResp?.data;

        // (Opcional) Registrar en BD para garantizar fila en TB_MEDIA_S3_META
        if (typeof metaMediaUpsert === "function") {
          try {
            const upsertResp = await metaMediaUpsert({
              wa_media_id: uploadData?.metaId,
              s3_filename: uploadData?.name,
              mime: uploadData?.mime,
              tipo: (uploadData?.mime || "").startsWith("video/") ? "V" : "I",
            });

            const upOk = !!(upsertResp?.data?.result ?? upsertResp?.result);
            if (!upOk) {
              const uMsg =
                upsertResp?.data?.message ||
                upsertResp?.message ||
                "Fallo al registrar media en la BD.";
              throw new Error(uMsg);
            }
          } catch (e) {
            // Permitimos continuar si tu API garantiza la fila por otro flujo
            console.warn(
              "metaMediaUpsert falló/ausente, continúo:",
              e?.message || e
            );
          }
        }
      } else {
        // Reutilizar lo existente (edición sin cambio de media)
        uploadData = {
          // metaId aceptará WA ID si existe; si no, null
          metaId:
            item?.plantilla?.wa_media_id ??
            item?.plantillaDraft?.wa_media_id ??
            item?.wa_media_id ??
            null,
          name: item?.archivo || null,
          mime: item?.mime || null,
        };
      }

      // -------- 2) Construir payload base y normalizar --------
      let payload = buildPlantillaPayloadFromItem(item, uploadData) || {};

      // Aseguramos idPlantilla en edición
      if (!payload.idPlantilla) {
        payload.idPlantilla =
          item?.plantilla?.idPlantilla || item?.idPlantilla || null;
      }

      // Forzamos header_tipo desde el item si vino explícito
      if (item?.header_tipo) payload.header_tipo = toUpper(item.header_tipo);

      payload = normalizePlantillaPayload(payload, uploadData, prevHeaderTipo);

      // Validación: si header es imagen/video y no quedó media enlazada
      if (
        (payload.header_tipo === "I" || payload.header_tipo === "V") &&
        !payload.wa_media_id &&
        !isInt(payload.idMedia)
      ) {
        throw new Error(
          "Debes seleccionar un archivo de media acorde al tipo de encabezado (Imagen/Video)."
        );
      }

      // -------- 3) Guardar plantilla --------
      const plantillaResp = await adminPlantilla(payload);
      const plantillaOk = !!(plantillaResp?.data?.result ?? plantillaResp?.result);
      if (!plantillaOk) {
        const msg =
          plantillaResp?.data?.message ||
          plantillaResp?.message ||
          "Error al guardar la plantilla.";
        throw new Error(msg);
      }

      const idPlantilla =
        plantillaResp?.data?.idPlantilla ??
        plantillaResp?.data ??
        plantillaResp?.idPlantilla;

      if (!idPlantilla) {
        throw new Error("No se recibió idPlantilla del servidor.");
      }

      // -------- 4) Asociar al contenido del micro --------
      const contenidoToMerge = {
        ...item,
        idPlantilla,
        archivo: String(idPlantilla), // Para .plantilla guardamos el ID en "archivo"
        formato: ".plantilla",
        file: undefined, // evita re-subir en adminContenidoMicroaprendizajes
        merge: true,
      };

      const contenidoRes = await adminContenidoMicroaprendizajes(
        contenidoToMerge
      );

      const contenidoOk = !!(
        contenidoRes?.data?.result ??
        contenidoRes?.result ??
        true
      );
      if (!contenidoOk) {
        const cmsg =
          contenidoRes?.data?.message ||
          contenidoRes?.message ||
          "Error al guardar el contenido del micro.";
        throw new Error(cmsg);
      }

      // -------- 5) Refrescar listado si aplica --------
      if (item?.idMicroaprendizaje) {
        await dispatch(
          fetchContenidoMicroaprendizaje({
            idMicroaprendizaje: item.idMicroaprendizaje,
          })
        );
      }

      // UI feedback
      dispatch(
        setResponse({
          result: true,
          message: isEdit ? "Plantilla actualizada" : "Plantilla creada",
        })
      );
      dispatch(setToast(true));
      setTimeout(() => dispatch(setToast(false)), 2500);

      return { idPlantilla };
    } catch (err) {
      const msg =
        err?.message || "Ocurrió un error creando/actualizando la plantilla.";
      console.error("createContenidoFromPlantilla error:", err);
      dispatch(setResponse({ result: -1, message: msg }));
      return rejectWithValue(msg);
    } finally {
      dispatch(setLoading(false));
      dispatch(setModal(false));
    }
  }
);


export const { setPlantilla, setPlantillas, updatePlantillaField, setBiblioteca } = metaSlice.actions;
export default metaSlice.reducer;